import { apiRequest } from './axiosConfig';

export interface RecommendedStyle {
  id: number;
  name: string;
  thumbnail_url: string | null;
  category_id: number | null;
  prompt_template: string | null;
  min_material_count?: number;
  max_material_count?: number | null;
}

export interface NoEligibleStyleGuidance {
  code: string;
  affected_style_count: number;
  checked_style_count: number;
  message: string;
}

export interface AutoStyleResponse {
  recommended: RecommendedStyle[];
  reason: string;
  category_slug: string | null;
  /** 候補が1件も無いときだけ入る、具体的な理由（不足している素材とその形状など）。 */
  no_eligible_style_reason?: string | null;
  no_eligible_style?: NoEligibleStyleGuidance | null;
  /** 商品情報を URL から取得中。候補は空で返る（取得が終わってから選び直す） */
  product_fetching?: boolean;
}

/**
 * 商材ジャンル + 共通スタイルプールからスタイル候補を取得。
 * 20パターン一括生成時、フロントでランダム割り当てする用途。
 */
export async function fetchAutoStyles(params: {
  productId?: number;
  inputText?: string;
  limit?: number;
}): Promise<AutoStyleResponse | null> {
  const query = new URLSearchParams();
  if (params.productId) query.append('product_id', String(params.productId));
  if (params.inputText) query.append('input_text', params.inputText);
  if (params.limit) query.append('limit', String(params.limit));

  try {
    // The route is registered under the `images` route group
    // (see api.php: prefix('images') -> /styles/auto-select), so the path
    // must be /images/styles/auto-select — omitting the prefix returns 405.
    return await apiRequest.get<AutoStyleResponse>(
      `/images/styles/auto-select?${query.toString()}`,
    );
  } catch {
    return null;
  }
}

/**
 * 推薦候補からランダムに1つ選ぶ。空なら null。
 */
export function pickRandomStyle(
  styles: RecommendedStyle[],
): RecommendedStyle | null {
  if (styles.length === 0) return null;
  const idx = Math.floor(Math.random() * styles.length);
  return styles[idx];
}

/**
 * 推薦候補から重複なしでランダムに複数選ぶ。
 */
export function pickRandomDistinctStyles<T extends { id: number }>(
  styles: T[],
  count: number,
  recentlyUsedStyleIds: number[] = [],
): T[] {
  const uniqueById = Array.from(
    new Map(styles.map((style) => [style.id, style])).values(),
  );

  for (let i = uniqueById.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [uniqueById[i], uniqueById[j]] = [uniqueById[j], uniqueById[i]];
  }

  const recentlyUsed = new Set(recentlyUsedStyleIds);
  const freshStyles = uniqueById.filter((style) => !recentlyUsed.has(style.id));
  const fallbackStyles = uniqueById.filter((style) => recentlyUsed.has(style.id));

  return [...freshStyles, ...fallbackStyles].slice(0, Math.max(0, count));
}

/**
 * Auto-style needs a wider pool than the number of output images; otherwise
 * shuffling changes only the order while returning the same styles every run.
 * Keep the request inside the backend's validated maximum of 30.
 */
export function getDiverseStyleCandidateLimit(imageCount: number): number {
  return Math.min(30, Math.max(12, imageCount * 3));
}

export type ReferenceImageCategory = 'brand_logo' | 'product_image' | 'material';

/**
 * スタイルの max_material_count に合わせて material 素材を先頭から最大 N 枚に絞り込む。
 *
 * - スタイル未指定 / max_material_count が null / 0 以下 のいずれかなら全件返す
 *   （0 は「制限なし」を意味する設計上の規約。0 枚スタイルは別フラグで表現する）
 * - material 以外 (brand_logo / product_image) は枚数制限の対象外
 * - 選択順を維持（呼び出し側で並び順 = ユーザーの優先順位 として渡してあること）
 *
 * `getCategory` を渡すと `type` 以外のキー（metadata 側の `category` など）にも使える。
 */
export function filterMaterialsByStyle<T>(
  refImages: T[],
  style: { max_material_count?: number | null } | null | undefined,
  getCategory: (img: T) => string = (img) => (img as { type: string }).type,
): T[] {
  const max = style?.max_material_count;
  // 0 は「制限なし」を意味する設計上の規約（「素材を使わない」スタイルは将来別フラグで表現する想定）。
  if (max == null || max <= 0) return refImages;

  let materialBudget = max;
  return refImages.filter((img) => {
    if (getCategory(img) !== 'material') return true;
    if (materialBudget <= 0) return false;
    materialBudget -= 1;
    return true;
  });
}
